import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../../config';
import './DecisionHistory.css';

interface Decision {
  id: number;
  decision_type: string;
  reason: string;
  triggered_by: string | null;
  workout_id: number | null;
  changes: Record<string, any> | null;
  created_at: string;
}

const BADGE_COLORS: Record<string, string> = {
  periodization: '#333',
  substitution: '#666',
  guardrail_override: '#999',
  workout_change: '#555',
};

const DecisionHistory: React.FC = () => {
  const [decisions, setDecisions] = useState<Decision[]>([]);
  const [loading, setLoading] = useState(true);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [collapsed, setCollapsed] = useState(true);
  const LIMIT = 20;

  const fetchDecisions = async (newOffset: number, append: boolean = false) => {
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/web/coach/decisions?limit=${LIMIT}&offset=${newOffset}`,
        { credentials: 'include' }
      );
      if (res.ok) {
        const data: Decision[] = await res.json();
        if (append) {
          setDecisions(prev => [...prev, ...data]);
        } else {
          setDecisions(data);
        }
        setHasMore(data.length === LIMIT);
      }
    } catch (e) {
      console.error('Failed to fetch decisions:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDecisions(0);
  }, []);

  const handleLoadMore = () => {
    const newOffset = offset + LIMIT;
    setOffset(newOffset);
    fetchDecisions(newOffset, true);
  };

  const toggleExpand = (id: number) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  if (loading) {
    return (
      <div className="decision-history-card">
        <div className="decision-history-loading">Loading decisions...</div>
      </div>
    );
  }

  if (decisions.length === 0) {
    return (
      <div className="decision-history-card">
        <h3 className="decision-history-title">Plan Changes</h3>
        <div className="decision-history-empty">No plan changes recorded yet.</div>
      </div>
    );
  }

  return (
    <div className="decision-history-card">
      <div className="decision-history-header" onClick={() => setCollapsed(!collapsed)}>
        <h3 className="decision-history-title">Plan Changes</h3>
        <span className="decision-history-count">{decisions.length} entries</span>
        <span className="decision-history-toggle">{collapsed ? '\u25B6' : '\u25BC'}</span>
      </div>

      {!collapsed && (
        <>
          <div className="decision-history-timeline">
            {decisions.map((d) => {
              const date = new Date(d.created_at);
              const dateStr = date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
              const timeStr = date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
              const badgeColor = BADGE_COLORS[d.decision_type] || '#777';
              const isExpanded = expanded.has(d.id);

              return (
                <div key={d.id} className="decision-history-entry">
                  <div className="decision-history-date">
                    <span className="decision-history-date-day">{dateStr}</span>
                    <span className="decision-history-date-time">{timeStr}</span>
                  </div>
                  <div className="decision-history-dot-line">
                    <div className="decision-history-dot" />
                    <div className="decision-history-line" />
                  </div>
                  <div className="decision-history-content">
                    <span
                      className="decision-history-badge"
                      style={{ background: badgeColor }}
                    >
                      {d.decision_type.replace(/_/g, ' ')}
                    </span>
                    <p className="decision-history-reason">{d.reason}</p>
                    {d.triggered_by && (
                      <span className="decision-history-trigger">Triggered by: {d.triggered_by}</span>
                    )}
                    {d.changes && Object.keys(d.changes).length > 0 && (
                      <button
                        className="decision-history-expand-btn"
                        onClick={() => toggleExpand(d.id)}
                      >
                        {isExpanded ? 'Hide details' : 'Show details'}
                      </button>
                    )}
                    {isExpanded && d.changes && (
                      <pre className="decision-history-changes">
                        {JSON.stringify(d.changes, null, 2)}
                      </pre>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {hasMore && (
            <button className="decision-history-load-more" onClick={handleLoadMore}>
              Load more
            </button>
          )}
        </>
      )}
    </div>
  );
};

export default DecisionHistory;
